import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-hot-toast';
import S from './HWFleetConfigurationStyles';
import useGetFleetId from '../../../../API/hooks/useGetFleetId';
import useGetDeviceConfigurationPerFleet from '../../../../API/hooks/devices/useGetDeviceConfigurationPerFleet';
import usePatchDeviceConfigurationPerFleet from '../../../../API/hooks/devices/usePatchDeviceConfigurationPerFleet';
import useGetDeviceDefaultConfiguration from '../../../../API/hooks/devices/useGetDeviceDefaultConfiguration';
import { DEFAULTS_UNAVAILABLE_MESSAGE, normalizeDeviceConfiguration } from '../../../../constants/DeviceConfig';
import { DeviceConfigurationFields } from '../../../../API/types/Devices';
import { Button } from '../../../../components/ui/Button/Button';
import CheckBox from '../../../../components/common/CheckBox/CheckBox';
import { alertEventsMap } from '../../../Alerts/constants/alertEventsMap';
import {
  speedLimitValidator,
  hardBrakeLvl1ThresholdValidator,
  hardAccelerationLvl1ThresholdValidator,
  highGLvl1ThresholdValidator,
  highGLvl2ThresholdValidator,
  hardTurnLvl1ThresholdValidator,
} from '../../../DeviceDetails/Modals/DeviceSettings/helpers';
import Modal from '../../../../components/common/Modal/Modal';
import InfoModal from '../../../../components/common/InfoModal/InfoModal';
import Input from '../../../../components/common/Input/Input';
import useGetFleetFeatures from '../../../../API/hooks/useGetFleetFeatures';

type Props = {
  toggleModal: (closed: boolean) => void;
};

const eventsEnableList1 = [
  'config/IMU/hard_brake/', // DevStreamEventHardBrake
  'config/IMU/hard_acceleration/', // DevStreamEventHardAcceleration
  'config/IMU/hard_turn/', // DevStreamEventHardTurn
  'config/IMU/high_g/', // DevStreamEventHighG
  'config/WIRE-CUT/wire_cut/', // Wirecutwarning
];

const eventsEnableList2 = [
  'config/ICDP/drowsiness/', // DevStreamEventDrowsiness
  'config/ICDP/eyes_of_the_road/', // DevStreamEventEyesOffRoad
  'config/ICDP/phone_usage/', // DevStreamEventMobilePhoneUsage
  'config/ECDP/front_collision_warning/', // DevStreamEventFcw
  'config/ECDP/pedestrian_collision_warning/', // DevStreamEventPcw
  'config/ECDP/lane_departure_warning/', // DevStreamEventLdw
  'config/ECDP/speed_limit_warning/', // DevStreamEventOverspeed
  'config/ECDP/tailgating_warning/', // DevStreamEventTailgaiting
  'config/ICDP/anti_spoofing/', // DevStreamDriverNotFound
  'config/ICDP/camera_blockage/', // DevStreamEventCameraBlockageInterior
  'config/ECDP/camera_blockage/', // DevStreamEventCameraBlockageExterior
];

const eventsActiveWarningList = [
  'config/IMU/hard_brake/', // DevStreamEventHardBrake ActiveWarning
  'config/IMU/hard_acceleration/', // DevStreamEventHardAcceleration ActiveWarning
  'config/IMU/hard_turn/', // DevStreamEventHardTurn ActiveWarning
  'config/ICDP/drowsiness/', // DevStreamEventDrowsiness ActiveWarning
  'config/ICDP/eyes_of_the_road/', // DevStreamEventEyesOffRoad ActiveWarning
  'config/ICDP/phone_usage/', // DevStreamEventEyesOffRoad ActiveWarning
  'config/ECDP/front_collision_warning/', // DevStreamEventFcw ActiveWarning
  'config/ECDP/pedestrian_collision_warning/', // DevStreamEventPcw ActiveWarning
  'config/ECDP/lane_departure_warning/', // DevStreamEventLdw ActiveWarning
  'config/ECDP/speed_limit_warning/', // DevStreamEventOverspeed ActiveWarning
  'config/WIRE-CUT/wire_cut/', // DevStreamEventWireCut
  'config/ECDP/tailgating_warning/', // DevStreamEventTailgaiting ActiveWarning
];

function HWFleetConfiguration({ toggleModal }: Props): JSX.Element {
  const [confirmSaveModalOpen, toggleConfirmSaveModal] = useState(false);
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): any => {
    toggleConfirmSaveModal(true);
    toast.success('Configuration was saved');
  };
  const onSubmitFail = (errorMessage: string): any => {
    toast.error(`Failed to save settings. Error: ${errorMessage}`);
    setSubmitError(errorMessage);
  };
  const fleetId = useGetFleetId();
  const { data: fleetConfig, isLoading } = useGetDeviceConfigurationPerFleet(fleetId);
  const { data: defaultConfig, isLoading: isLoadingDefaults } = useGetDeviceDefaultConfiguration();
  const defaults = defaultConfig?.configuration;
  const isLoadingConfig = isLoading || isLoadingDefaults;
  const { mutate } = usePatchDeviceConfigurationPerFleet(onSubmitSuccess, onSubmitFail);
  const [allEnableChecked, setAllEnableChecked] = useState(false);
  const [allActiveWarningChecked, setAllActiveWarningChecked] = useState(false);
  const [fleetIsActive, setFleetIsActive] = useState(fleetConfig?.isActive !== undefined ? fleetConfig.isActive : false);
  const { videoRecording: videoRecordingEnabled } = useGetFleetFeatures();

  const formik = useFormik({
    // Seeded from the service defaults once they arrive — see the effect below. The form is not
    // rendered until then, so it is never shown with these empty values.
    initialValues: {} as DeviceConfigurationFields,
    onSubmit: (values) => {
      if (!defaults) return;
      normalizeDeviceConfiguration(defaults, values);
      mutate({
        fleetId,
        isActive: fleetIsActive,
        configuration: normalizeDeviceConfiguration(defaults, values),
      });
    },
    validate: (values) => {
      const errors = {} as Record<string, string>;

      // validateOnMount fires on the first render, before the fetched defaults have seeded the
      // form, and the checks below read their key unconditionally. Nothing to validate until then.
      if (!Object.keys(values).length) return errors;

      if (values['config/ECDP/speed_limit_warning/threshold_speed'] !== undefined && !speedLimitValidator.test(values['config/ECDP/speed_limit_warning/threshold_speed'].toString())) {
        errors['config/ECDP/speed_limit_warning/threshold_speed'] = 'Can contain only numbers in range from 5 to 200 km/h';
      }

      if (values['config/IMU/hard_brake/lvl_1_threshold'] !== undefined && !hardBrakeLvl1ThresholdValidator.test(values['config/IMU/hard_brake/lvl_1_threshold'].toString())) {
        errors['config/IMU/hard_brake/lvl_1_threshold'] = 'Only numbers from -20 to 0 (step 0.1)';
      }

      if (values['config/IMU/hard_turn/lvl_1_threshold'] !== undefined && !hardTurnLvl1ThresholdValidator.test(values['config/IMU/hard_turn/lvl_1_threshold'].toString())) {
        errors['config/IMU/hard_turn/lvl_1_threshold'] = 'Only numbers from 0 to 20 (step 0.1)';
      }

      if (values['config/IMU/hard_acceleration/lvl_1_threshold'] !== undefined && !hardAccelerationLvl1ThresholdValidator.test(values['config/IMU/hard_acceleration/lvl_1_threshold'].toString())) {
        errors['config/IMU/hard_acceleration/lvl_1_threshold'] = 'Only numbers from 0 to 20 (step 0.1)';
      }

      if (values['config/IMU/high_g/lvl_1_threshold'] !== undefined && !highGLvl1ThresholdValidator.test(values['config/IMU/high_g/lvl_1_threshold'].toString())) {
        errors['config/IMU/high_g/lvl_1_threshold'] = 'Only numbers from 0 to 20 (step 0.1)';
      }

      if (values['config/IMU/high_g/lvl_2_threshold'] !== undefined && !highGLvl2ThresholdValidator.test(values['config/IMU/high_g/lvl_2_threshold'].toString())) {
        errors['config/IMU/high_g/lvl_2_threshold'] = 'Only numbers from 0 to 20 (step 0.1)';
      }

      return errors;
    },
    validateOnMount: true,
  });

  // `formik.values` starts empty and is seeded asynchronously by the effect below. Every field
  // reads `formik.values[...].toString()` unconditionally, so rendering before it is populated
  // throws during render and unmounts the whole app — the form waits for this instead.
  const valuesReady = Object.keys(formik.values).length > 0;

  const checkAllSelected = (type: 'activeWarning' | 'enable'): boolean => {
    if (type === 'activeWarning') {
      return eventsActiveWarningList.every((n) => formik.values[`${n}active_warning` as keyof typeof formik.values] !== false);
    }
    return eventsEnableList1.every((n) => formik.values[`${n}enabled` as keyof typeof formik.values] !== false) && eventsEnableList2.every((n) => formik.values[`${n}enable` as keyof typeof formik.values] !== false);
  };

  useEffect(() => {
    formik.setSubmitting(false);
    if (formik.values) {
      setAllEnableChecked(checkAllSelected('enable'));
      setAllActiveWarningChecked(checkAllSelected('activeWarning'));
    }
  }, [formik.values]);

  useEffect(() => {
    if (!defaults) return;
    formik.setValues(normalizeDeviceConfiguration(defaults, fleetConfig?.configuration), false);
    setFleetIsActive(fleetConfig?.isActive !== undefined ? fleetConfig.isActive : false);
  }, [fleetConfig, defaults]);

  const setDefaultValues = (): void => {
    if (!defaults) return;
    formik.setValues(defaults, false);
  };

  const handleIsActiveChange = (id: string, state: boolean): void => {
    setFleetIsActive(state);
    formik.setSubmitting(false);
  };

  const handleCheckboxChange = (id: string, state: boolean): void => {
    formik.setFieldValue(id, state);
    formik.setSubmitting(false);
    setSubmitError('');
  };
  const handleAllCheckboxChange = (id: string, state: boolean): void => {
    if (id === 'enable') {
      setAllEnableChecked(state);
      eventsEnableList1.forEach((n: string) => formik.setFieldValue(`${n}enabled`, state));
      eventsEnableList2.forEach((n: string) => formik.setFieldValue(`${n}enable`, state));
    } else if (id === 'activeWarning') {
      setAllActiveWarningChecked(state);
      eventsActiveWarningList.forEach((n: string) => formik.setFieldValue(`${n}active_warning`, state));
    }
    formik.setSubmitting(false);
    setSubmitError('');
  };

  const handleInputChange = (e: any): void => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  useEffect(() => {
    formik.setSubmitting(false);
  }, [submitError, formik.values]);

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="device-configuration">
          {(isLoadingConfig || !formik.values) && (
            <div className="loading">Configuration is loading...</div>
          )}
          {(!isLoadingConfig && !defaults) && (
            <p className="warning">{DEFAULTS_UNAVAILABLE_MESSAGE}</p>
          )}
          {(!isLoadingConfig && !!defaults && valuesReady) && (
            <>
              <S.Row>
                <p> </p>
                <CheckBox
                  label="Select all"
                  name="enable"
                  state={allEnableChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Select all"
                  name="activeWarning"
                  state={allActiveWarningChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <h4>Events:</h4>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventFcw.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/front_collision_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/front_collision_warning/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/front_collision_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/front_collision_warning/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventPcw.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/pedestrian_collision_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/pedestrian_collision_warning/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/pedestrian_collision_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/pedestrian_collision_warning/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventLdw.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/lane_departure_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/lane_departure_warning/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/lane_departure_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/lane_departure_warning/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventOverspeed.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/speed_limit_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/speed_limit_warning/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/speed_limit_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/speed_limit_warning/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventWireCut.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/WIRE-CUT/wire_cut/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/WIRE-CUT/wire_cut/enabled"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/WIRE-CUT/wire_cut/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/WIRE-CUT/wire_cut/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventTailgaiting.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/tailgating_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/tailgating_warning/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/tailgating_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/tailgating_warning/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventDrowsiness.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/drowsiness/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/drowsiness/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ICDP/drowsiness/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/drowsiness/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventEyesOffRoad.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/eyes_of_the_road/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/eyes_of_the_road/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ICDP/eyes_of_the_road/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/eyes_of_the_road/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventCameraBlockageInterior.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/camera_blockage/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/camera_blockage/enable"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventCameraBlockageExterior.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/camera_blockage/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/camera_blockage/enable"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventMobilePhoneUsage.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/phone_usage/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/phone_usage/enable"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ICDP/phone_usage/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/phone_usage/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHighG.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/high_g/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/high_g/enabled"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHardBrake.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/hard_brake/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_brake/enabled"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/IMU/hard_brake/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_brake/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHardAcceleration.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/hard_acceleration/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_acceleration/enabled"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/IMU/hard_acceleration/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_acceleration/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHardTurn.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/hard_turn/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_turn/enabled"
                  disabled={isLoading || !fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/IMU/hard_turn/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_turn/active_warning"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamDriverNotFound.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/anti_spoofing/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/anti_spoofing/enable"
                  disabled={isLoading || !fleetIsActive}
                />
              </S.Row>
              <div className="divider"> </div>
              <S.Row>
                <h4>Common configurations:</h4>
              </S.Row>
              <S.RowContainer>
                <div className="input-component">
                  <CheckBox
                    label="GPS enabled"
                    state={formik.values['config/GPS/localization/enable']}
                    onChange={handleCheckboxChange}
                    name="config/GPS/localization/enable"
                    disabled={isLoading || !fleetIsActive}
                  />
                </div>
                <div className="input-component">
                  <CheckBox
                    label="Inward camera video recording enabled"
                    state={formik.values['config/Video/recording/enabled_icam']}
                    onChange={handleCheckboxChange}
                    name="config/Video/recording/enabled_icam"
                    disabled={isLoading || !videoRecordingEnabled || !fleetIsActive}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <CheckBox
                    label="Driver identification enabled"
                    state={formik.values['config/E3D/driver_id/enable']}
                    onChange={handleCheckboxChange}
                    name="config/E3D/driver_id/enable"
                    disabled={isLoading || !fleetIsActive}
                  />
                </div>
                <div className="input-component">
                  <CheckBox
                    label="Outward camera video recording enabled"
                    state={formik.values['config/Video/recording/enabled_ecam']}
                    onChange={handleCheckboxChange}
                    name="config/Video/recording/enabled_ecam"
                    disabled={isLoading || !videoRecordingEnabled || !fleetIsActive}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <p className={!videoRecordingEnabled ? 'message warning' : 'hidden'}>
                  Warning! Video recording is disabled. Please contact your administractor to turn on.
                </p>
              </S.RowContainer>
              <S.Row>
                <h4>Events threshold configurations:</h4>
              </S.Row>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Vehicle Speed Limit (km/h)"
                    type="text"
                    key="config/ECDP/speed_limit_warning/threshold_speed"
                    name="config/ECDP/speed_limit_warning/threshold_speed"
                    placeholder="Type value from 5 to 200"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaults['config/ECDP/speed_limit_warning/threshold_speed'].toString()}
                    value={formik.values['config/ECDP/speed_limit_warning/threshold_speed'].toString()}
                    disabled={isLoading || !fleetIsActive}
                    error={formik.touched['config/ECDP/speed_limit_warning/threshold_speed'] && formik.errors['config/ECDP/speed_limit_warning/threshold_speed']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Hard Brake Threshold"
                    type="text"
                    key="config/IMU/hard_brake/lvl_1_threshold"
                    name="config/IMU/hard_brake/lvl_1_threshold"
                    placeholder="Type value"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaults['config/IMU/hard_brake/lvl_1_threshold'].toString()}
                    value={formik.values['config/IMU/hard_brake/lvl_1_threshold'].toString()}
                    disabled={isLoading || !fleetIsActive}
                    error={formik.touched['config/IMU/hard_brake/lvl_1_threshold'] && formik.errors['config/IMU/hard_brake/lvl_1_threshold']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Hard Turn Threshold"
                    type="text"
                    key="config/IMU/hard_turn/lvl_1_threshold"
                    name="config/IMU/hard_turn/lvl_1_threshold"
                    placeholder="Type value"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaults['config/IMU/hard_turn/lvl_1_threshold'].toString()}
                    value={formik.values['config/IMU/hard_turn/lvl_1_threshold'].toString()}
                    disabled={isLoading || !fleetIsActive}
                    error={formik.touched['config/IMU/hard_turn/lvl_1_threshold'] && formik.errors['config/IMU/hard_turn/lvl_1_threshold']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Hard Acceleration Threshold"
                    type="text"
                    key="config/IMU/hard_acceleration/lvl_1_threshold"
                    name="config/IMU/hard_acceleration/lvl_1_threshold"
                    placeholder="Type value"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaults['config/IMU/hard_acceleration/lvl_1_threshold'].toString()}
                    value={formik.values['config/IMU/hard_acceleration/lvl_1_threshold'].toString()}
                    disabled={isLoading || !fleetIsActive}
                    error={formik.touched['config/IMU/hard_acceleration/lvl_1_threshold'] && formik.errors['config/IMU/hard_acceleration/lvl_1_threshold']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="High G Threshold L1"
                    type="text"
                    key="config/IMU/high_g/lvl_1_threshold"
                    name="config/IMU/high_g/lvl_1_threshold"
                    placeholder="Type value"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaults['config/IMU/high_g/lvl_1_threshold'].toString()}
                    value={formik.values['config/IMU/high_g/lvl_1_threshold'].toString()}
                    disabled={isLoading || !fleetIsActive}
                    error={formik.touched['config/IMU/high_g/lvl_1_threshold'] && formik.errors['config/IMU/high_g/lvl_1_threshold']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="High G Threshold L2"
                    type="text"
                    key="config/IMU/high_g/lvl_2_threshold"
                    name="config/IMU/high_g/lvl_2_threshold"
                    placeholder="Type value"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaults['config/IMU/high_g/lvl_2_threshold'].toString()}
                    value={formik.values['config/IMU/high_g/lvl_2_threshold'].toString()}
                    disabled={isLoading || !fleetIsActive}
                    error={formik.touched['config/IMU/high_g/lvl_2_threshold'] && formik.errors['config/IMU/high_g/lvl_2_threshold']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer className={submitError ? '' : 'hidden'}>
                <p className="message error">Submitting error: {typeof submitError === 'string' ? submitError : submitError?.map((error) => error)}</p>
              </S.RowContainer>
              <div className="divider"> </div>
              <S.Row>
                <CheckBox
                  label="Fleet configuration is active"
                  state={fleetIsActive}
                  onChange={handleIsActiveChange}
                  name="isActive"
                  disabled={isLoading}
                />
              </S.Row>
            </>
          )}
        </div>
        <S.Buttons>
          <Button
            className="close-button"
            height="50px"
            onClick={() => toggleModal(false)}
            type="button"
          >
            Close
          </Button>
          <Button
            height="50px"
            width="160px"
            onClick={() => setDefaultValues()}
            type="button"
            disabled={!fleetIsActive}
          >
            Set Default
          </Button>
          <Button
            className="save-button"
            height="50px"
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || !!submitError || isLoading}
          >
            Save
          </Button>
        </S.Buttons>
      </form>
      {confirmSaveModalOpen && (
        <Modal
          title="Device settings"
          state={confirmSaveModalOpen}
          toggleState={toggleConfirmSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="New settings were submitted!"
            onClose={() => toggleConfirmSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default HWFleetConfiguration;
